'use client';
import { queryFeeds } from '@/apis/land/land.api';
import { cookieUtil } from '@/intl-jdi-common-ssr/utils/cookieUtils';
import ProductCard from '@/components/productCard';
import ArrowDown from '@/assets/icons/ArrowDown.svg';
import { useTranslation } from '@/i18n/client';
import { useStores } from '@/models';
import { getExposureRecord, sendClickTrack } from '../../../tempo_track';
import { formatProductLink } from '@/utils';
import { useEffect, useRef, useState } from 'react';
import style from './productList.module.scss';
interface ListProps {
  skuList: any;
  paramsData: any;
  dataExternalKey: string;
  layout: number;
  pos: number;
  pageCode: string;
  localInfo: any;
  moduleInstanceId: string;
}
export const ProductList = ({
  skuList,
  paramsData,
  dataExternalKey,
  layout,
  pos,
  pageCode,
  localInfo,
  moduleInstanceId,
}: ListProps) => {
  const [productList, setProductList] = useState<any>([...skuList]);
  const [loading, setLoading] = useState(false); //是否在加载数据
  const [isShowTryAgain, setIsShowTryAgain] = useState(false); //是否展示tryAgain
  const cuPageRef = useRef(2);
  const { t } = useTranslation();
  const lastScrollIdRef = useRef<number>(skuList[skuList.length - 1]?.extInfo?.scrollId);
  const stopGetProductRef = useRef(skuList?.[skuList.length - 1]?.extInfo?.lastSkuTag || false); //是否停止获取数据
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );
  const [width, setWidth] = useState(0);
  const { cartNumStore } = useStores();

  // ---------------- 计算屏幕宽度 ---------------- //
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // ---------------- 按照layout计算商卡宽度 ---------------- //
  useEffect(() => {
    if (layout === 2) {
      setWidth(screenWidth / 2 - (12 * 3) / 2);
    } else {
      setWidth(screenWidth / 3 - (12 * 4) / 3);
    }
  }, [screenWidth]);

  // ---------------- 获取更多商品信息 ---------------- //
  const getMoreProduct = async () => {
    if (stopGetProductRef.current || loading) {
      return;
    }
    setLoading(true);
    setIsShowTryAgain(false);
    // 构建请求参数
    const _params = {
      type: dataExternalKey,
      cuPage: cuPageRef.current,
      pageSize: 2 * layout,
      mapExInfo: {
        ...paramsData,
        lastScrollId: lastScrollIdRef.current,
      },
    };
    // 如果没有缓存数据，请求推荐商品
    const res = await queryFeeds(_params).catch((err) => {
      console.log(err);
    });
    if (res) {
      const list = res.data.map((sku: any, index) => {
        sku.url = formatProductLink(sku.skuId, sku.skuName);
        if (index === res.data.length - 1) {
          lastScrollIdRef.current = sku?.extInfo?.scrollId;
          stopGetProductRef.current = sku?.extInfo?.lastSkuTag || false;
        }
        return sku;
      });
      setProductList([...productList, ...list]);
      cuPageRef.current = res.currentPage + 1;
    }
    setLoading(false);
  };

  // ---------------- 更新购物车浮层数据 ---------------- //
  const getCartNum = () => {
    cartNumStore.fetchCartNum(true);
  };

  //------------------------ 商品&加购 点击和曝光 埋点 -------------------------

  const genJsonParam = (product: any, index: number) => {
    const json_param: any = {
      page_id: pageCode,
      moduleInstanceId: moduleInstanceId,
      pos: pos,
      index: index,
      skuid: isNaN(product?.skuId) ? '-100' : product.skuId.toString(),
      propoolid: paramsData?.productPoolId,
      card_type: layout === 2 ? '0' : '1',
    };
    if (product.algoRdInfo) {
      json_param.propoolid = product.productPoolId;
      json_param.broker_info = product.brokerInfo ? JSON.stringify(product.brokerInfo) : '';
      json_param.algo_rd_inf = product.algoRdInfo;
    }
    if (paramsData?.type === 'EXT_CHANNEL_ACTIVITY_PRODUCT') {
      const attributionCookie = cookieUtil.getCookie('_attribution');
      const attribution = attributionCookie ? JSON.parse(attributionCookie) : { utm: {} };
      json_param.channelName = attribution.utm.utm_source ? attribution.utm.utm_source : 'organic';
      json_param.activityid = paramsData.productPoolId;
    }
    return json_param;
  };
  const exposureRecord = getExposureRecord(pageCode);

  const genEventTrackingInfo = (product: any, index: number) => {
    const eventTrackingInfo = {} as { [key: string]: any };
    const json_param = genJsonParam(product, index);
    const expConfig = {
      useClick: false,
      stay_duration: 0.3,
      repeated: false,
      area_rate: 0.5,
    };

    eventTrackingInfo['productClickPoint'] = () =>
      sendClickTrack.cms_TempoPage_3or2ProductPool_click(json_param);
    eventTrackingInfo['cartClickPoint'] = () =>
      sendClickTrack.cms_TempoPage_3or2ProductPool_addtocart_click(json_param);
    eventTrackingInfo['productExpConfig'] = {
      event_id: exposureRecord.cms_TempoPage_3or2ProductPool_expo,
      config: expConfig,
      json_param: json_param,
    };

    return eventTrackingInfo;
  };
  return (
    width !== 0 && (
      <div className={style.container}>
        <div className={style.productList}>
          {productList.length
            ? productList.map((item: any, index: number) => {
                const productData = {
                  type: '1',
                  width: `${width}`,
                  skuName: item.skuName,
                  shouldShowSkuName: true,
                  jumpUrl: item?.jumpUrl ? item?.jumpUrl : null,
                  skuId: item.skuId,
                  realPrice: item.realPrice,
                  originalPrice: item?.originalPriceV2,
                  originalPriceTips: item?.originalPriceDesc,
                  currency: localInfo?.currencyCode || '',
                  languageCode: localInfo?.languageCode || '',
                  cartChange: getCartNum,
                  // 3行布局不展示购物车
                  shouldShowCart: layout === 2 ? true : false,
                  trackingPointParams: genEventTrackingInfo(item, index),
                  // style
                  containerStyle: {
                    backgroundColor: '#fff',
                    marginBottom: '10px',
                  },
                  skuImgKeyParams: {
                    imgUrlKey: item?.imageUrl,
                    width: 200,
                    height: 200,
                  },
                  skuImgStyle: {
                    width: `${width}`,
                    height: `${width}`,
                    borderRadius: '4px',
                    alignSelf: 'center',
                  },
                  skuNameStyle: {
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    textOverflow: 'ellipsis',
                    whiteSpace: 'normal',
                    height: '36px',
                    marginTop: '-6px',
                    overflow: 'hidden',
                    color: '#1a1a1a',
                    wordBreak: 'break-all',
                    paddingRight: layout === 2 ? '8px' : '0px',
                    paddingLeft: layout === 2 ? '8px' : '0px',
                  },
                  priceAndCartStyle: {
                    paddingTop: '8px',
                    paddingRight: layout === 2 ? '8px' : '0px',
                    paddingLeft: layout === 2 ? '8px' : '0px',
                  },
                  realPriceJDIPriceParams: {
                    symbolStyle: { fontSize: layout === 2 ? '16px' : '14px' },
                    integerStyle: { fontSize: layout === 2 ? '20px' : '18px' },
                    decimalPointStyle: { fontSize: layout === 2 ? '20px' : '18px' },
                    decimalPlaceStyle: { fontSize: layout === 2 ? '20px' : '18px' },
                    containerStyle: {
                      display: 'block',
                      height: '20px',
                      lineHeight: '20px',
                      fontWeight: '700',
                      color: '#1a1a1a',
                      marginBottom: '2px',
                    },
                  },
                  originalPriceJDIPriceParams: {
                    containerStyle: {
                      height: '12px',
                      fontWeight: '500',
                      lineHeight: '12px',
                      fontSize: layout === 2 ? '12px' : '10px',
                      color: '#888b94',
                    },
                  },
                  originalPriceStyle: {
                    marginTop: '6px',
                    fontSize: layout === 2 ? '12px' : '10px',
                  },
                  cartIconColor: 'black',
                  cartIconStyle: {
                    width: '24px',
                    height: '24px',
                  },
                };
                // @ts-ignore
                return <ProductCard key={index} productData={productData} />;
              })
            : null}
        </div>
        {!stopGetProductRef.current && (
          <div className={style.viewMore} onClick={() => getMoreProduct()}>
            <p>{t('common.btnText.viewMore')}</p>
            <ArrowDown width={16} height={16} color={'#888B94'} />
          </div>
        )}
      </div>
    )
  );
};
